// app/javascript/Pages/DanceStyles/EditDanceStyle.tsx

import React, { useState } from 'react';
import { useForm } from '@mantine/form';
import { Inertia } from '@inertiajs/inertia';
import { usePage } from '@inertiajs/react';
import { TextInput, Textarea, Button, Box, Image, Text, Group, Modal } from '@mantine/core';
import { IconCheck, IconAlertCircle, IconUpload, IconPhoto, IconX } from '@tabler/icons-react';
import FormLayout from '../../components/FormLayout';
import { Notifications, showNotification } from '@mantine/notifications';
import ImageUploadModal from '../../components/ImageUploadModal';
import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';

interface DanceStyleData {
  id: number;
  name?: string;
  slug?: string;
  seo_image_url?: string | null;
  seo_image?: File | null;
}

interface EditDanceStyleProps extends InertiaPageProps {
  dance_style: DanceStyleData;
  errors?: string[];
}

function slugify(str: string): string {
  return str
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-') // replace whitespace with dashes
    .replace(/[^\w-]+/g, ''); // remove non-word chars
}

export default function EditDanceStyle() {
  const { dance_style, errors } = usePage<EditDanceStyleProps>().props;
  const [values, setValues] = useState<DanceStyleData>(dance_style);

  const [modalOpen, setModalOpen] = useState(false);

  console.log('Seo Image URL:', dance_style.seo_image_url);

  const [isLoading, setIsLoading] = useState(false);

  // If you want to handle new image + crop:

  const [seoImagePreview, setSeoImagePreview] = useState<string | null>(dance_style.seo_image_url);
  const [isHovering, setIsHovering] = useState(false);

  const handleImageChange = (file: File) => {
    console.log('📸 Image selected:', file);

    setValues((prevValues) => {
      const newValues = { ...prevValues, seo_image: file };
      console.log('🆕 Updated state:', newValues); // 🔍 Debugging new state
      return newValues;
    });

    setSeoImagePreview(URL.createObjectURL(file));
  };

  // Mantine form with existing values:
  const form = useForm({
    initialValues: {
      name: dance_style.name || '',
      slug: dance_style.slug || '',
    },
    validate: {
      name: (value) => (value ? null : 'Name is required'),
    },
  });

  // Auto-generate slug if name changes
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    form.setFieldValue('name', newName);
    form.setFieldValue('slug', slugify(newName));
  };

  // Form submit
  const handleSubmit = async (values: typeof form.values) => {
    console.log('📍 Submitting form with values:', values);
    setIsLoading(true);
    const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content;

    try {
      const formData = new FormData();
      formData.append('dance_style[name]', values.name);
      formData.append('dance_style[slug]', values.slug);

      if (values.seo_image instanceof File) {
        formData.append('dance_style[seo_image]', values.seo_image);
      } else {
        console.log('😬 No image file detected'); // Debugging
      }

      // ✅ Send PUT request with CSRF token
      const response = await fetch(`/dance_styles/${values.slug}`, {
        method: 'PUT',
        headers: {
          Accept: 'application/json',
          'X-CSRF-Token': csrfToken || '',
        },
        credentials: 'include',
        body: formData,
      });

      if (response.ok) {
        showNotification({
          color: 'green',
          title: 'Update Successful',
          position: 'top-right',
          message: 'Dance style was successfully updated!',
          icon: <IconCheck />,
          autoClose: 6000,
        });

        // ✅ Redirect to the updated dance style page after success
        setTimeout(() => {
          window.location.href = `/dance_styles/${values.slug}`;
        }, 1500);
      } else {
        const data = await response.json().catch(() => ({}));
        showNotification({
          color: 'red',
          title: 'Update Failed',
          position: 'top-right',
          message: data.error || 'Something went wrong!',
          icon: <IconAlertCircle />,
          autoClose: 5000,
        });

        // Show validation errors if available
        if (data.errors) {
          form.setErrors(data.errors);
        }
      }
    } catch (error) {
      showNotification({
        color: 'red',
        title: 'Error',
        position: 'top-right',
        message: 'An unexpected error occurred',
        icon: <IconAlertCircle />,
        autoClose: 5000,
      });
      console.error('Error updating dance style:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <FormLayout title="Edit Dance Style">
      <form onSubmit={form.onSubmit(() => handleSubmit({ ...form.values, seo_image: values.seo_image }))}>
        {/* If there's an image preview, show it with an overlay icon to trigger the modal.
          Otherwise, if no image exists, show the dropzone directly in the main form. */}
        <ImageUploadModal previewUrl={seoImagePreview} onImageChange={handleImageChange} />

        <TextInput
          label="Name"
          value={form.values.name}
          onChange={(e) => setValues({ ...values, name: e.target.value })}
          mt="md"
          required
        />
        <TextInput
          label="Slug"
          value={form.values.slug}
          onChange={(e) => form.setFieldValue('slug', e.target.value)}
          mt="md"
        />

        {/* Server-side errors */}
        {errors && errors.length > 0 && (
          <Box mt="md" style={{ color: 'red' }}>
            {errors.map((err, i) => (
              <div key={i}>{err}</div>
            ))}
          </Box>
        )}

        <Button type="submit" mt="md">
          Update Dance Style
        </Button>
      </form>
    </FormLayout>
  );
}
